// # Clinical Rotations Guide Component
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MedicineBoxOutlined, 
  TeamOutlined, 
  FileProtectOutlined, 
  SafetyCertificateOutlined,
  RocketOutlined,
  MedicineBoxFilled
} from '@ant-design/icons';
import DashboardHeader from '../../../components/DashboardHeader';
import Header from '../../../landing/components/header';  // # Import regular header
import DashboardFooter from '../../../components/DashboardFooter';
import './rotation_main.css';
import { auth, db } from '../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';
import { useAuthStore } from '../../../auth/state';  // # Import auth store

const RotationsGuide = () => {
  const navigate = useNavigate();
  const sectionRefs = useRef([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { isAuthenticated } = useAuthStore();  // # Get auth state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Helper function to handle scroll animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // # Add animation class to the section itself
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          
          // # Add animation to QA items
          const qaItems = entry.target.querySelectorAll('.rot_mn_qa-item');
          qaItems.forEach((item, i) => {
            const baseDelay = 0.1;
            const staggerDelay = i * 0.15;
            item.style.setProperty('--delay', `${baseDelay + staggerDelay}s`);
            item.style.opacity = '1';
            item.style.transform = 'translateX(0)';
            item.classList.add('rot_mn_animate');
          });
          
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    sectionRefs.current.forEach(section => section && observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // # Updated sections with USCE content
  const sections = [
    {
      title: 'Getting Started with USCE',
      icon: <MedicineBoxOutlined className="rot_mn_section-icon" />,
      questions: [
        {
          q: 'What is USCE and why is it important for IMGs?',
          a: '<span class="rot_mn_key-term">USCE (United States Clinical Experience)</span> is clinical training in the U.S. that helps <span class="rot_mn_key-term">International Medical Graduates (IMGs)</span> gain experience, adapt to the U.S. healthcare system, and secure <span class="rot_mn_highlight">Letters of Recommendation (LORs)</span> for residency. It significantly improves residency chances, provides <span class="rot_mn_key-term">EMR training</span>, and helps build valuable connections in the U.S. medical community.'
        },
        {
          q: 'What are the different types of USCE available?',
          a: 'There are two main types:<ul><li><span class="rot_mn_key-term">Clinical Electives:</span> Hands-on patient care under supervision <span class="rot_mn_secondary">(available only for medical students)</span></li><li><span class="rot_mn_key-term">Observerships:</span> Non-hands-on shadowing of physicians without direct patient interaction <span class="rot_mn_secondary">(available for both students and graduates)</span></li></ul>'
        },
        {
          q: 'Are observerships useful for residency applications?',
          a: '<span class="rot_mn_highlight">Yes, while hands-on experience is preferred</span>, observerships are valuable as they provide exposure to the U.S. healthcare system and can lead to strong Letters of Recommendation. They are particularly important for graduates who cannot participate in clinical electives.'
        },
        {
          q: 'What is VSLO and who can use it?',
          a: '<span class="rot_mn_key-term">VSLO (Visiting Student Learning Opportunities)</span> is a system connecting international students to U.S. university hospital rotations. <span class="rot_mn_warning">It\'s only available for current medical students, not graduates.</span> In Egypt, only <span class="rot_mn_highlight">Tanta, Cairo, and Ain Shams universities</span> are VSLO affiliated.'
        }
      ]
    },
    {
      title: 'Application Process & Requirements',
      icon: <FileProtectOutlined className="rot_mn_section-icon" />,
      questions: [
        {
          q: 'How do I apply for clinical experiences?',
          a: '<div class="rot_mn_list-header">For Clinical Electives:</div><ul><li>Through VSLO if your school is affiliated</li><li>Direct hospital applications</li></ul><br/><div class="rot_mn_list-header">For Observerships:</div><ul><li>Through hospital programs</li><li>Private companies</li><li>Direct physician contact</li></ul>'
        },
        {
          q: 'What documents are typically required for applications?',
          a: '<ul><li>Updated CV/Resume</li><li>Medical school transcripts</li><li>Immunization records</li><li>Dean\'s letter (for electives)</li><li>USMLE Step 1 scores (some programs)</li><li>Visa documentation (typically B1/B2)</li><li>English proficiency proof (TOEFL/IELTS for some programs)</li></ul>'
        },
        {
          q: 'What are the visa requirements for USCE?',
          a: '<span class="rot_mn_highlight">Most programs require a B1/B2 visitor visa.</span> Ensure your visa allows for clinical observations or rotations. <span class="rot_mn_secondary">Some programs may provide visa assistance or documentation support.</span>'
        },
        {
          q: 'Do I need USMLE Step 1 to apply for rotations?',
          a: '<span class="rot_mn_highlight">Not always.</span> While some programs require Step 1 scores, many accept applications without USMLE scores. However, having a passing Step 1 score can make your application stronger and provide more opportunities.'
        }
      ]
    },
    {
      title: 'Costs & Program Selection',
      icon: <TeamOutlined className="rot_mn_section-icon" />,
      questions: [
        {
          q: 'How much does USCE typically cost?',
          a: 'Costs vary significantly:<ul><li><span class="rot_mn_cost-item">University-affiliated programs: $500-$2000/month</span></li><li><span class="rot_mn_cost-item">Private companies: $2000-$5000/month</span></li><li><span class="rot_mn_cost-item">Additional costs: Housing, transportation, visa fees</span></li></ul><br/><span class="rot_mn_highlight">VSLO-affiliated programs are generally more affordable than private companies.</span>'
        },
        {
          q: 'Are private companies like AMO worth considering?',
          a: 'Private companies provide opportunities but have limitations:<ul><li><span class="rot_mn_warning">Often more expensive</span></li><li><span class="rot_mn_warning">Usually clinic-based rather than hospital-based</span></li><li><span class="rot_mn_warning">May provide weaker LORs compared to university programs</span></li><li>Can be useful when other options aren\'t available</li></ul>'
        },
        {
          q: 'What about tele-observerships?',
          a: '<span class="rot_mn_warning">While tele-observerships emerged during the pandemic, they are considered much weaker than in-person rotations.</span> They can provide some exposure to the U.S. healthcare system but should not be your primary USCE if in-person options are available.'
        },
        {
          q: 'How do I choose between different programs?',
          a: 'Consider these factors:<ul><li><span class="rot_mn_key-term">Program reputation</span></li><li><span class="rot_mn_key-term">Hospital vs. clinic setting</span></li><li><span class="rot_mn_key-term">Cost and duration</span></li><li><span class="rot_mn_key-term">Location and living expenses</span></li><li><span class="rot_mn_key-term">Opportunity for hands-on experience</span></li><li><span class="rot_mn_key-term">Likelihood of strong LORs</span></li><li><span class="rot_mn_key-term">Research opportunities</span></li></ul>'
        }
      ]
    },
    {
      title: 'Best Practices & Success Tips',
      icon: <SafetyCertificateOutlined className="rot_mn_section-icon" />,
      questions: [
        {
          q: 'How can I make the most of my rotation?',
          a: '<ul><li>Be proactive and show initiative</li><li>Arrive early and be well-prepared</li><li>Study cases before rounds</li><li>Engage actively in discussions</li><li>Build relationships with team members</li><li>Ask thoughtful questions</li><li>Offer to help with research or projects</li><li>Seek regular feedback</li><li>Maintain professional behavior and appearance</li></ul>'
        },
        {
          q: 'What about Letters of Recommendation (LORs)?',
          a: 'To secure strong LORs:<ul><li>Perform consistently well throughout the rotation</li><li>Build rapport with attending physicians</li><li>Keep a log of interesting cases</li><li>Request LORs in person near the end of rotation</li><li>Provide your CV and personal statement</li><li>Follow up professionally</li></ul><div class="rot_mn_note">LORs from academic hospitals typically carry more weight than those from private clinics.</div>'
        },
        {
          q: 'Are research opportunities available during rotations?',
          a: 'Research opportunities are more common in university-affiliated programs. To pursue research:<ul><li>Express interest early</li><li>Look for ongoing projects</li><li>Propose ideas based on your observations</li><li>Be willing to continue collaboration remotely</li></ul>'
        },
        {
          q: 'What common mistakes should I avoid?',
          a: '<ul><li>Don\'t be passive or uninvolved</li><li>Avoid being late or unprepared</li><li>Don\'t neglect professional appearance</li><li>Don\'t wait until the last minute to request LORs</li><li>Avoid focusing solely on observation without engagement</li><li>Don\'t miss opportunities to network</li></ul>'
        }
      ]
    }
  ];

  return (
    <div className={`rot_mn_page-container ${isDarkMode ? 'rot_mn_dark-mode' : ''}`}>
      {isAuthenticated ? (
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      ) : (
        <Header />
      )}

      <div className="rot_mn_content-container">
        <div className="rot_mn_header">
          <div className="rot_mn_header-content">
            <div className="rot_mn_header-title">
              <div className="rot_mn_icon-container">
                <MedicineBoxFilled style={{ fontSize: '3rem', color: '#1a365d' }} />
              </div>
              <h1>USCE Guide</h1>
            </div>
            <div className="rot_mn_header-subtitle">
              <p>Your comprehensive guide to U.S. Clinical Experience</p>
            </div>
          </div>
        </div>

        <div className="rot_mn_sections">
          {sections.map((section, index) => (
            <div 
              key={index} 
              className="rot_mn_section"
              ref={el => sectionRefs.current[index] = el}
            >
              <div className="rot_mn_section-header">
                <div className="rot_mn_icon-container">
                  {section.icon}
                </div>
                <h2>{section.title}</h2>
              </div>
              
              <div className="rot_mn_qa-list">
                {section.questions.map((qa, qaIndex) => (
                  <div key={qaIndex} className="rot_mn_qa-item">
                    <div className="rot_mn_question">
                      <strong>Q{qaIndex + 1}:</strong> {qa.q}
                    </div>
                    <div className="rot_mn_answer" 
                         dangerouslySetInnerHTML={{ __html: qa.a }}>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* # Add CTA Section */}
          <div className="rot_mn_cta-section">
            <div className="rot_mn_cta-content">
              <div className="rot_mn_cta-icon">
                <RocketOutlined />
              </div>
              <h2>Ready to Excel in Your USMLE Journey?</h2>
              <p>We offer personalized and perfectly-designed mentorship programs for USMLE Step 1 & 2</p>
              <button 
                className="rot_mn_cta-button"
                onClick={() => navigate('/products')}
              >
                Explore Our Programs
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="rot_mn_footer-wrapper">
        <DashboardFooter isDarkMode={isDarkMode} />
      </div>
    </div>
  );
};

export default RotationsGuide;
